<template>
  <b-modal
    id="po-import-chassis-modal"
    v-model="show"
    title="Import số khung / số máy"
    size="xl"
    hide-footer
    no-close-on-backdrop
  >
    <div class="po-import">
      <b-tabs v-model="activeTab" content-class="mt-3">
        <!-- ===== Tab 1: Nhập tay ===== -->
        <b-tab title="Nhập tay" active>
          <div
            class="d-flex justify-content-between align-items-center mb-2 flex-wrap"
          >
            <span class="po-import-summary">
              Đã nhập tay:
              <b class="text-primary">{{ manualValidCount }}</b> — Tổng đặt:
              <b>{{ totalOrdered }}</b>
            </span>
            <b-button
              size="sm"
              variant="outline-primary"
              class="text-nowrap"
              @click="addManualRow"
            >
              <i class="flaticon2-add-1 po-import-icon"></i> Thêm dòng
            </b-button>
          </div>

          <table class="table table-bordered po-manual-table">
            <thead>
              <tr>
                <th style="width: 36px" class="text-center">#</th>
                <th>Số khung <span class="text-danger">*</span></th>
                <th>Số máy</th>
                <th v-if="multiModel" style="width: 28%">Model</th>
                <th style="width: 44px"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(r, idx) in manualRows"
                :key="r._key"
                :class="{ 'po-manual-row-error': manualRowError(r, idx) }"
              >
                <td class="text-center align-middle">{{ idx + 1 }}</td>
                <td>
                  <b-form-input
                    v-model="r.chassisNumber"
                    size="sm"
                    class="mono"
                    placeholder="Nhập số khung"
                  />
                </td>
                <td>
                  <b-form-input
                    v-model="r.engineNumber"
                    size="sm"
                    class="mono"
                    placeholder="(tùy chọn)"
                  />
                </td>
                <td v-if="multiModel">
                  <b-form-select
                    v-model="r.purchaseOrderItemId"
                    :options="modelOptions"
                    size="sm"
                  />
                </td>
                <td class="text-center align-middle">
                  <b-button
                    size="sm"
                    variant="link"
                    class="text-danger p-0"
                    @click="removeManualRow(idx)"
                  >
                    <i class="fas fa-trash po-import-icon"></i>
                  </b-button>
                </td>
              </tr>
              <tr v-if="!manualRows.length">
                <td
                  :colspan="multiModel ? 5 : 4"
                  class="text-center text-muted py-3"
                >
                  Chưa có dòng nào. Bấm "Thêm dòng" để nhập số khung.
                </td>
              </tr>
            </tbody>
          </table>
          <p v-if="manualError" class="text-danger small mb-0">
            {{ manualError }}
          </p>
        </b-tab>

        <!-- ===== Tab 2: Import Excel (giữ nguyên) ===== -->
        <b-tab title="Import Excel">
          <div class="po-import-upload d-flex align-items-center">
            <b-form-file
              v-model="file"
              accept=".xlsx"
              size="sm"
              class="po-import-file"
              placeholder="Chọn file Excel (.xlsx)..."
              drop-placeholder="Thả file vào đây..."
              browse-text="Chọn file"
              @input="onFileSelected"
            />
            <b-button
              class="ml-2 text-nowrap"
              size="sm"
              variant="info"
              :disabled="!file || previewing"
              @click="doPreview"
            >
              <b-spinner v-if="previewing" small class="mr-1"></b-spinner>
              Xem trước
            </b-button>
          </div>
          <p class="po-import-hint text-muted">
            File .xlsx tối đa 200 dòng. Cột bắt buộc: <b>Số khung</b>.
            <b>Số máy</b> có thể để trống (nhập sau). File mẫu đã điền sẵn
            <b>Mã model</b> theo PO — chỉ cần gõ Số khung (+ Số máy). Mã model
            hợp lệ: <b class="text-primary">{{ modelCodes.join(', ') }}</b
            >. Hệ thống kiểm tra trùng / sai trước khi lưu.
            <b-link class="po-import-template ml-1" @click="downloadTemplate">
              <i class="fas fa-download po-import-template-icon"></i>
              Tải file mẫu
            </b-link>
          </p>

          <!-- Preview table -->
          <div v-if="rows.length" class="po-import-preview">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="po-import-summary">
                Tổng: {{ rows.length }} — Hợp lệ:
                <b class="text-success">{{ successCount }}</b> — Lỗi:
                <b class="text-danger">{{ errorCount }}</b>
              </span>
            </div>
            <b-table
              :items="rows"
              :fields="fields"
              responsive
              bordered
              small
              :tbody-tr-class="rowClass"
              class="po-import-table"
            >
              <template #cell(row)="r">{{ r.item.row }}</template>
              <template #cell(chassisNumber)="r">
                <span class="mono">{{ r.item.chassisNumber }}</span>
              </template>
              <template #cell(engineNumber)="r">
                <span
                  v-if="isEnginePlaceholder(r.item.engineNumber)"
                  class="text-muted font-italic"
                >
                  (chưa có)
                </span>
                <span v-else class="mono">{{ r.item.engineNumber }}</span>
              </template>
              <template #cell(model)="r">
                <span class="po-import-model">
                  {{ rowModelLabel(r.item.purchaseOrderItemId) }}
                </span>
              </template>
              <template #cell(status)="r">
                <b-badge :variant="r.item.status === 1 ? 'success' : 'danger'">
                  <i
                    :class="[
                      'po-import-icon',
                      r.item.status === 1
                        ? 'fas fa-check'
                        : 'fas fa-exclamation-triangle',
                    ]"
                  ></i>
                  {{ r.item.status === 1 ? 'Hợp lệ' : 'Lỗi' }}
                </b-badge>
              </template>
              <template #cell(message)="r">
                <span :class="{ 'text-danger': r.item.status !== 1 }">
                  {{ r.item.message || '' }}
                </span>
              </template>
            </b-table>
          </div>
        </b-tab>
      </b-tabs>

      <b-alert v-if="errorMessage" variant="danger" show class="mt-2">
        {{ errorMessage }}
      </b-alert>

      <!-- Footer actions — áp theo tab đang active -->
      <div class="d-flex justify-content-end mt-3">
        <b-button class="mr-2" size="sm" variant="secondary" @click="close">
          Đóng
        </b-button>
        <b-button
          v-if="isManualTab"
          size="sm"
          variant="primary"
          :disabled="!canSaveManual || importing"
          @click="saveManual"
        >
          <b-spinner v-if="importing" small class="mr-1"></b-spinner>
          Lưu ({{ manualValidCount }})
        </b-button>
        <b-button
          v-else
          size="sm"
          variant="primary"
          :disabled="!canImport || importing"
          @click="doImport"
        >
          <b-spinner v-if="importing" small class="mr-1"></b-spinner>
          Lưu ({{ successCount }})
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { get } from 'lodash';
import xlsx from 'xlsx';

import PurchaseOrderService from '@/core/services/purchaseOrder.service';
import { PO_IMPORT_ROW_STATUS } from '@/core/constants/purchaseOrder';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';

export default {
  name: 'ImportChassisModal',
  props: {
    poId: { type: String, required: true },
    // items của PO — chưa dùng trực tiếp nhưng giữ để mở rộng (map dòng PO).
    items: { type: Array, default: () => [] },
  },
  data() {
    return {
      show: false,
      activeTab: 0, // 0 = Nhập tay (mặc định), 1 = Import Excel
      file: null,
      previewing: false,
      importing: false,
      rows: [],
      errorMessage: '',
      fields: [
        { key: 'row', label: 'Dòng', class: 'text-center' },
        { key: 'chassisNumber', label: 'Số khung' },
        { key: 'engineNumber', label: 'Số máy' },
        { key: 'model', label: 'Sản phẩm' },
        { key: 'status', label: 'Kết quả', class: 'text-center' },
        { key: 'message', label: 'Ghi chú' },
      ],
      // Nhập tay
      manualRows: [],
      manualError: '',
      manualKeySeq: 0,
      // Items của PO (ưu tiên prop, fallback fetch khi open). Dùng để render
      // select Model + auto-gán purchaseOrderItemId.
      poItems: [],
    };
  },
  computed: {
    successCount() {
      return this.rows.filter((r) => r.status === PO_IMPORT_ROW_STATUS.SUCCESS)
        .length;
    },
    errorCount() {
      return this.rows.filter((r) => r.status !== PO_IMPORT_ROW_STATUS.SUCCESS)
        .length;
    },
    // Chỉ cho phép Import khi có ít nhất 1 dòng SUCCESS và KHÔNG còn dòng lỗi.
    canImport() {
      return (
        this.rows.length > 0 && this.successCount > 0 && this.errorCount === 0
      );
    },
    isManualTab() {
      return this.activeTab === 0;
    },
    // Nguồn item: ưu tiên poItems (đã fetch), fallback prop items.
    resolvedItems() {
      const list =
        this.poItems && this.poItems.length ? this.poItems : this.items;
      return Array.isArray(list) ? list : [];
    },
    multiModel() {
      return this.resolvedItems.length > 1;
    },
    // Mã model (product.code) trong PO — dùng cho file mẫu + hướng dẫn Excel nhiều model.
    modelCodes() {
      return this.resolvedItems
        .map((it) => get(it, 'product.code') || it.productCode || '')
        .filter(Boolean);
    },
    modelOptions() {
      return this.resolvedItems.map((it) => ({
        value: it.id,
        text: this.itemLabel(it),
      }));
    },
    totalOrdered() {
      return this.resolvedItems.reduce(
        (sum, it) => sum + (Number(it.quantityOrdered) || 0),
        0,
      );
    },
    // Dòng hợp lệ = có số khung + (đã chọn item nếu multi-model) + không trùng.
    manualValidCount() {
      return this.manualRows.filter((r, idx) => this.manualRowValid(r, idx))
        .length;
    },
    canSaveManual() {
      return (
        this.manualRows.length > 0 &&
        this.manualValidCount > 0 &&
        // Không còn dòng lỗi (trùng / thiếu) trong các dòng đã gõ số khung.
        !this.manualRows.some((r, idx) => this.manualRowError(r, idx))
      );
    },
  },
  methods: {
    open() {
      this.reset();
      this.show = true;
      this.loadItems();
    },
    close() {
      this.show = false;
    },
    reset() {
      this.activeTab = 0;
      this.file = null;
      this.rows = [];
      this.errorMessage = '';
      this.previewing = false;
      this.importing = false;
      this.manualRows = [];
      this.manualError = '';
      this.manualKeySeq = 0;
      // Bắt đầu với 1 dòng nhập tay sẵn.
      this.addManualRow();
    },
    // Lấy items PO: prop trước, fetch detail nếu prop rỗng. Sau khi có items,
    // auto-gán item cho các dòng manual nếu PO chỉ 1 model.
    async loadItems() {
      if (this.items && this.items.length) {
        this.poItems = this.items;
        this.autoAssignSingleModel();
        return;
      }
      try {
        const res = await PurchaseOrderService.getById(this.poId);
        const po = get(res, 'data.data') || get(res, 'data') || {};
        this.poItems = Array.isArray(po.items) ? po.items : [];
      } catch (err) {
        this.poItems = [];
      } finally {
        this.autoAssignSingleModel();
      }
    },
    // PO 1 model → gán sẵn purchaseOrderItemId cho mọi dòng manual.
    autoAssignSingleModel() {
      if (this.resolvedItems.length === 1) {
        const itemId = this.resolvedItems[0].id;
        this.manualRows.forEach((r) => {
          if (!r.purchaseOrderItemId) r.purchaseOrderItemId = itemId;
        });
      }
    },
    itemLabel(it) {
      const name = get(it, 'product.name') || it.productName || '';
      const code = get(it, 'product.code') || it.productCode || '';
      if (code && name) return `${code} - ${name}`;
      return name || code || it.productId || it.id;
    },
    // Tên sản phẩm/model của 1 dòng preview theo purchaseOrderItemId.
    rowModelLabel(itemId) {
      if (!itemId) return '—';
      const it = this.resolvedItems.find((x) => x.id === itemId);
      return it ? this.itemLabel(it) : '—';
    },
    addManualRow() {
      // PO 1 model → auto-gán item; multi-model → để user chọn.
      const defaultItemId =
        this.resolvedItems.length === 1 ? this.resolvedItems[0].id : null;
      this.manualRows.push({
        _key: this.manualKeySeq++,
        chassisNumber: '',
        engineNumber: '',
        purchaseOrderItemId: defaultItemId,
      });
    },
    removeManualRow(idx) {
      this.manualRows.splice(idx, 1);
    },
    // Trùng số khung trong danh sách nhập tay (so sánh không phân biệt hoa/thường,
    // bỏ qua ô trống).
    isDuplicateManual(row, idx) {
      const cur = (row.chassisNumber || '').trim().toLowerCase();
      if (!cur) return false;
      return this.manualRows.some(
        (r, i) =>
          i !== idx && (r.chassisNumber || '').trim().toLowerCase() === cur,
      );
    },
    // Dòng hợp lệ: có số khung, không trùng, (đã chọn model nếu multi-model).
    manualRowValid(row, idx) {
      const hasChassis = !!(row.chassisNumber || '').trim();
      if (!hasChassis) return false;
      if (this.isDuplicateManual(row, idx)) return false;
      if (this.multiModel && !row.purchaseOrderItemId) return false;
      return true;
    },
    // Dòng LỖI (đỏ): đã gõ số khung nhưng trùng / thiếu model. Dòng trống hoàn
    // toàn không tính là lỗi (chưa nhập).
    manualRowError(row, idx) {
      const hasChassis = !!(row.chassisNumber || '').trim();
      if (!hasChassis) return false;
      if (this.isDuplicateManual(row, idx)) return true;
      if (this.multiModel && !row.purchaseOrderItemId) return true;
      return false;
    },
    onFileSelected() {
      // Đổi file → xóa preview cũ.
      this.rows = [];
      this.errorMessage = '';
    },
    // Generate file Excel mẫu client-side bằng `xlsx`. Header + 2 dòng ví dụ.
    // "Dòng PO" để trống = hệ thống tự gán khi PO chỉ có 1 model.
    downloadTemplate() {
      // File mẫu sinh SẴN theo nội dung PO: mỗi model có đủ số dòng = số xe còn cần
      // nhập (quantityOrdered − quantityImported), cột "Mã model" điền sẵn → người dùng
      // chỉ gõ Số khung (+ Số máy). Header KHỚP đúng tên cột BE parse.
      const aoa = [['Số khung', 'Số máy', 'Mã model']];
      this.resolvedItems.forEach((it) => {
        const code = get(it, 'product.code') || it.productCode || '';
        const ordered = Number(it.quantityOrdered) || 0;
        const imported = Number(it.quantityImported) || 0;
        const remaining = Math.max(0, ordered - imported);
        for (let i = 0; i < remaining; i++) {
          // LUÔN điền sẵn Mã model (kể cả PO 1 model) → mẫu thống nhất, rõ ràng.
          aoa.push(['', '', code]);
        }
      });
      // Fallback nếu PO chưa có dòng / đã nhập đủ → vẫn cho vài dòng kèm mã model đầu.
      if (aoa.length === 1) {
        const c = this.modelCodes[0] || '';
        aoa.push(['', '', c], ['', '', c]);
      }
      const ws = xlsx.utils.aoa_to_sheet(aoa);
      ws['!cols'] = [{ wch: 22 }, { wch: 18 }, { wch: 24 }];
      const wb = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(wb, ws, 'SoKhung');
      xlsx.writeFile(wb, 'mau-import-so-khung.xlsx');
    },
    rowClass(item) {
      if (!item) return '';
      return item.status === PO_IMPORT_ROW_STATUS.SUCCESS
        ? ''
        : 'po-import-row-error';
    },
    // Số máy "chưa có" = rỗng hoặc placeholder toàn ký tự x/X.
    isEnginePlaceholder(v) {
      const s = (v || '').toString().trim();
      return !s || /^x+$/i.test(s);
    },
    async doPreview() {
      if (!this.file) return;
      this.previewing = true;
      this.errorMessage = '';
      this.rows = [];
      try {
        const formData = new FormData();
        formData.append('file', this.file);
        const res = await PurchaseOrderService.previewChassis(
          this.poId,
          formData,
        );
        // BE preview trả { poId, rows, hasError } → lấy .rows (không phải mảng trực tiếp).
        const payload = get(res, 'data.data') || get(res, 'data') || {};
        const list = Array.isArray(payload) ? payload : payload.rows;
        this.rows = Array.isArray(list) ? list : [];
        if (!this.rows.length) {
          this.errorMessage = 'File không có dòng dữ liệu hợp lệ nào.';
        }
      } catch (err) {
        this.errorMessage =
          get(err, 'response.data.message') ||
          err.message ||
          'Xem trước thất bại';
      } finally {
        this.previewing = false;
      }
    },
    // Lưu tab Nhập tay → build rows SUCCESS rồi dùng chung endpoint import.
    async saveManual() {
      this.manualError = '';
      this.errorMessage = '';
      if (!this.canSaveManual) {
        this.manualError = 'Vui lòng kiểm tra lại các dòng (trùng / thiếu).';
        return;
      }
      const validRows = this.manualRows.filter((r, idx) =>
        this.manualRowValid(r, idx),
      );
      const rows = validRows.map((r) => ({
        chassisNumber: (r.chassisNumber || '').trim(),
        engineNumber: (r.engineNumber || '').trim() || undefined,
        purchaseOrderItemId: r.purchaseOrderItemId,
        status: PO_IMPORT_ROW_STATUS.SUCCESS,
      }));
      this.importing = true;
      try {
        await PurchaseOrderService.importChassis(this.poId, rows);
        makeToastSuccess(`Đã thêm ${rows.length} số khung`);
        this.show = false;
        this.$emit('imported');
      } catch (err) {
        const msg =
          get(err, 'response.data.message') || err.message || 'Lưu thất bại';
        this.errorMessage = msg;
        makeToastFaile(msg);
      } finally {
        this.importing = false;
      }
    },
    async doImport() {
      if (!this.canImport) return;
      this.importing = true;
      this.errorMessage = '';
      try {
        const successRows = this.rows.filter(
          (r) => r.status === PO_IMPORT_ROW_STATUS.SUCCESS,
        );
        await PurchaseOrderService.importChassis(this.poId, successRows);
        makeToastSuccess(`Đã import ${successRows.length} số khung`);
        this.show = false;
        this.$emit('imported');
      } catch (err) {
        const msg =
          get(err, 'response.data.message') || err.message || 'Import thất bại';
        this.errorMessage = msg;
        makeToastFaile(msg);
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.po-import {
  .po-import-upload {
    display: flex;
    align-items: center;
  }
  // b-form-file chiếm phần còn lại; nút "Xem trước" giữ chiều cao input.
  .po-import-file {
    flex: 1 1 auto;
    min-width: 0;
  }
  .po-import-hint {
    font-size: 11px;
    margin-top: 6px;
    margin-bottom: 12px;
  }
  .po-import-template {
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }
  .po-import-template-icon {
    font-size: 10px;
    margin-right: 2px;
    color: inherit !important;
  }
  .po-import-summary {
    font-size: 13px;
  }
  .mono {
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
  }
  .po-import-icon {
    font-size: 9px;
    margin-right: 2px;
    color: inherit !important;
  }
  // Bảng nhập tay
  .po-manual-table {
    margin-bottom: 8px;
    th {
      background-color: #f3f6f9;
      font-size: 12px;
      vertical-align: middle;
    }
    td {
      vertical-align: middle;
    }
  }
  // Dòng lỗi (trùng / thiếu model) — tô đỏ nhạt.
  .po-manual-row-error > td {
    background-color: #ffe2e5;
  }
}
</style>

<style lang="scss">
// Không scope: cần tô đỏ <tr> do b-table render (rowClass áp lên <tr>).
#po-import-chassis-modal .po-import-row-error > td {
  background-color: #ffe2e5 !important;
  color: #f64e60;
}
</style>
